import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2265" data-mp="gmtgmc62mgad">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2266" data-mp="cmhvky9sqdyd">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2267" data-mp="7ogcidcdeemw">
          <div className="flex items-center h-16" data-id="element-2268" data-mp="7prhgghjxsfl">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2269" data-mp="2vwzbhhlhlim">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2270" data-mp="3u1gamjfovirl">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2271" data-mp="3p60ypx18rwxt">
                <Home size={18} data-id="element-2272" data-mp="3g3rn13u8lxiu" />
                <span className="hidden sm:inline" data-id="element-2273" data-mp="3g3hzro0ku4gu">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2274" data-mp="1vm8rnzw3l0wv">
                <BarChart3 size={18} data-id="element-2275" data-mp="20iz6yl02vsh8" />
                <span className="hidden sm:inline" data-id="element-2276" data-mp="24mnfb5wjas3j">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2277" data-mp="29li5wu0qtky7">
                <FileText size={18} data-id="element-2278" data-mp="1cw98tuquoi1w" />
                <span className="hidden sm:inline" data-id="element-2279" data-mp="1ct4wga38eg05">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2280" data-mp="1gx2usikqfms5">
                <UserCog size={18} data-id="element-2281" data-mp="1qn5vz620c71k" />
                <span className="hidden sm:inline" data-id="element-2282" data-mp="1qne7atihf4an">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2283" data-mp="y6bmzlge84fv">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2284" data-mp="y4iioct95wb1">
                <Bell size={20} data-id="element-2285" data-mp="30qmectzoanhu" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2286" data-mp="2vufhdqp98mc1"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2287" data-mp="39mc5ljnsyc35">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2288" data-mp="34ulu3ad4pia5">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2289" data-mp="3jhncxgwvsh0v">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2290" data-mp="3siv65kkjiro3">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2291" data-mp="1y65gxg54a6n1" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2292" data-mp="1u51z4o3ie0j6">
                    <div className="py-2" data-id="element-2293" data-mp="1k1wolyz4hz8l">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2294" data-mp="1k2zfi6wg41oi">
                        <Users size={18} className="text-gray-600" data-id="element-2295" data-mp="1aa0oyjhe2129" />
                        <span className="font-medium text-gray-900" data-id="element-2296" data-mp="1f9dan0wix239">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2297" data-mp="10eqm5tj61pnf">
                        <Home size={18} className="text-gray-600" data-id="element-2298" data-mp="rchb92jzju9i" />
                        <span className="font-medium text-gray-900" data-id="element-2299" data-mp="reak8oi2mqko">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2300" data-mp="2lqib9qr9unb6">
                        <UserCog size={18} className="text-gray-600" data-id="element-2301" data-mp="2oa587crrx9sh" />
                        <span className="font-medium text-gray-900" data-id="element-2302" data-mp="2o8alpu0ut9ma">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2303" data-mp="2fai42j7ntpvq">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2304" data-mp="37ye8yb7duo3l">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-2305" data-mp="2y8kzbfqswj1z"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-2306" data-mp="72da0eleolcf"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2307" data-mp="c16q8es2uqga">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2308" data-mp="g28nwr6ciug5"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2309" data-mp="l0tgq5j9qbuk">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2310" data-mp="puo69plk1l1k">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2311" data-mp="zoo9tn16ui86"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2312" data-mp="13svy5j3qhw5v"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2313" data-mp="1p29p5xfdthrf"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2314" data-mp="1l0o4hpwlvz6o">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2315" data-mp="16dl9bpvra2mi">
        <div data-phoebe-tour="profile" className="mb-8" data-id="element-2316" data-mp="11e8kizlbqlha">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2317" data-mp="11bnia9h463j0">Profile</h1>
          <p className="text-gray-600" data-id="element-2318" data-mp="1g47gqwtbjmu4">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2319" data-mp="1ba390bc8barz">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-2320" data-mp="njz7c9ilpl3m">
            <nav className="space-y-1 sticky top-24" data-id="element-2321" data-mp="io0kiqcx8b5b">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2322" data-mp="ilefaxdi67q9">
                <User size={18} data-id="element-2323" data-mp="2lbh3plw7sa6p" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2324" data-mp="2pcj5oahin0cd">
                <Shield size={18} data-id="element-2325" data-mp="340npjhsb1hwq" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2326" data-mp="342qbnqqc6qw6">
                <Globe size={18} data-id="element-2327" data-mp="1xlmrn6jpypni" />
                Preferences
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-2328" data-mp="3i4mg78pvkycv">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2329" data-mp="34dhl8jxgcu8v">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2330" data-mp="2ud78f0fbxou8">
                  <div className="p-6 border-b border-gray-200" data-id="element-2331" data-mp="2zewjuh6gs4iu">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2332" data-mp="2levlb8do996j">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2333" data-mp="2qf16q6tclzg6">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-2334" data-mp="ok90riujkj1p">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-2335" data-mp="yqa81sz1h2z5">
                      <div className="relative group" data-id="element-2336" data-mp="tqnvllr1bqre">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-2337" data-mp="ts8q5bdcn9p9">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-2338" data-mp="12q152ef5sfc4" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-2339" data-mp="1hfqcgrduhfku">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-2340" data-mp="1cgdlbufxxmcg">
                            <Camera className="text-white" size={24} data-id="element-2341" data-mp="1mg4ik3vvyfg8" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2342" data-mp="3s8b91fntpq20" />
                      </div>
                      <div data-id="element-2343" data-mp="1r7d2ghhfn5fk">
                        <h3 className="font-medium text-gray-900" data-id="element-2344" data-mp="1r86d3tjiif2z">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-2345" data-mp="213gdkcu9ieen">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-2346" data-mp="260q27q9tum1f">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-2347" data-mp="2a3ms821p5kfe">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2348" data-mp="2f1eikrmp0yhh" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-2349" data-mp="2overz5p0cpw0">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2350" data-mp="m882a7pyxg52">
                      <div data-id="element-2351" data-mp="i4ili4fjc2ul">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2352" data-mp="i7ei9pfnclhg">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2353" data-mp="885pv10fjex7" />
                      </div>
                      <div data-id="element-2354" data-mp="w41efpzrz7mb">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2355" data-mp="w38g9devtqfs">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2356" data-mp="1olvtk6nn016b" />
                      </div>
                    </div>

                    <div data-id="element-2357" data-mp="18faido8vbqu8">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2358" data-mp="18fkeltkiau8d">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-2359" data-mp="zcrk7hg122eb">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2360" data-mp="zfnix7zp8v3t" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-2361" data-mp="1n5hulm8a8dmh" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2362" data-mp="1icfas5tb4ijh">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2363" data-mp="2asg8cc50p7gp">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2364" data-mp="3vivfgndok1m2">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2365" data-mp="44zem71qxhdj" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2366" data-mp="3ckj0gl037xik" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2367" data-mp="3gl2zju481b7c">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2368" data-mp="vxq0popptpp2">
                  <div className="p-6 border-b border-gray-200" data-id="element-2369" data-mp="3ppy35yvrbbhu">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2370" data-mp="3uldi6u5i3dfu">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2371" data-mp="2h2uo7ty0glg">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2372" data-mp="36yylliqld3yl">
                    <div data-id="element-2373" data-mp="3ay7fcnlmt1u5">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2374" data-mp="3azigefnozvhk">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2375" data-mp="3ko8qkesvyprn">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2376" data-mp="3kq3dfynpwryd" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-2377" data-mp="2n8uvmtsohbdl" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2378" data-mp="2ap7ms6wmzb24">
                      <div data-id="element-2379" data-mp="26pp31egjx60v">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2380" data-mp="26odznfr0q0aa">
                          New Password
                        </label>
                        <div className="relative" data-id="element-2381" data-mp="1wym2qevg4v2n">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2382" data-mp="1wxt5q5mahjxp" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-2383" data-mp="1rxyfm4ac8ydp" />
                        </div>
                      </div>
                      <div data-id="element-2384" data-mp="31y952tzjicmn">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2385" data-mp="31uv7s05qgtm2">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-2386" data-mp="2svhtmu6nzgv0">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2387" data-mp="2wxl6nn8zetie" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-2388" data-mp="2j5yadnf1hkzm" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-2389" data-mp="207c6y1w1vhsj">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-2390" data-mp="2576y86jqm9e4">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-2391" data-mp="1ll5gjx9k7dbh">
                        <li data-id="element-2392" data-mp="1gndfacn0yeum">Minimum 8 characters long</li>
                        <li data-id="element-2393" data-mp="1um3b7oy3y8he">At least one uppercase letter</li>
                        <li data-id="element-2394" data-mp="1uj8otrvjadlc">At least one number</li>
                        <li data-id="element-2395" data-mp="1qix5qjitklbz">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2396" data-mp="24hohw0e4fwxh">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2397" data-mp="1zika9e6ui799">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-2398" data-mp="2ibo532vsuq5g">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-2399" data-mp="2utctf17px64r">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-2400" data-mp="2qsauymlmky3u">
                      <AlertTriangle size={20} data-id="element-2401" data-mp="2qun72j4pce8z" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-2402" data-mp="2zsz7fcvdeqem">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-2403" data-mp="2c3o35letadvq">
                    <div data-id="element-2404" data-mp="275wa2o2fklpy">
                      <h3 className="font-medium text-gray-900" data-id="element-2405" data-mp="272gwjpoenkqn">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2406" data-mp="2h1zgw78898j8">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-2407" data-mp="1oebxuwmlupyp">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2408" data-mp="3v1t1btfohuf2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2409" data-mp="rkasbio66hd2">
                  <div className="p-6 border-b border-gray-200" data-id="element-2410" data-mp="dkt3j3avj43p">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2411" data-mp="ik5zqenmxniy">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2412" data-mp="2yb0kuuou20cr">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2413" data-mp="18v7qt65rfl0f">
                    <div data-id="element-2414" data-mp="18twmn58ekb8s">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2415" data-mp="13vldkwpxm4rm">
                        Language
                      </label>
                      <div className="relative" data-id="element-2416" data-mp="z3k5zo3pl27x">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2417" data-mp="v39p3gedorea" />
                        <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-2418" data-mp="q3x3t035nsdt">
                          <option value="en" data-id="element-2419" data-mp="g83gqmrt8ghs">English (US)</option>
                          <option value="es" data-id="element-2420" data-mp="c5gtz09zxlrg">Español</option>
                          <option value="fr" data-id="element-2421" data-mp="2ajrzl015x37g">Français</option>
                          <option value="de" data-id="element-2422" data-mp="2aj8sked1vz87">Deutsch</option>
                          <option value="zh" data-id="element-2423" data-mp="26i6sjvbb7j56">中文 (Chinese)</option>
                          <option value="ja" data-id="element-2424" data-mp="3og33t1gmz9z6">日本語 (Japanese)</option>
                        </select>
                      </div>
                    </div>

                    <div data-id="element-2425" data-mp="3tdv3wirfq2eg">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2426" data-mp="3td1u18x6iws3">
                        Time Zone
                      </label>
                      <div className="relative" data-id="element-2427" data-mp="62bzu1c9s5qk">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2428" data-mp="646ita3zdns0" />
                        <select value={timezone} onChange={e => setTimezone(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-2429" data-mp="35jjy6m51k47d">
                          <option value="UTC-8" data-id="element-2430" data-mp="3jijp60g10v6p">
                            Pacific Time (US & Canada) (UTC-08:00)
                          </option>
                          <option value="UTC-7" data-id="element-2431" data-mp="yqhnowymeaum">
                            Mountain Time (US & Canada) (UTC-07:00)
                          </option>
                          <option value="UTC-6" data-id="element-2432" data-mp="x9ucp0rrrwuf">
                            Central Time (US & Canada) (UTC-06:00)
                          </option>
                          <option value="UTC-5" data-id="element-2433" data-mp="16as6kq8hunza">
                            Eastern Time (US & Canada) (UTC-05:00)
                          </option>
                          <option value="UTC+0" data-id="element-2434" data-mp="127dqjzkk27lu">
                            Greenwich Mean Time (UTC+00:00)
                          </option>
                          <option value="UTC+1" data-id="element-2435" data-mp="1b9vcse07n08u">
                            Central European Time (UTC+01:00)
                          </option>
                          <option value="UTC+8" data-id="element-2436" data-mp="1pxq8825lripm">
                            China Standard Time (UTC+08:00)
                          </option>
                          <option value="UTC+9" data-id="element-2437" data-mp="1yyobujc4po7o">
                            Japan Standard Time (UTC+09:00)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2438" data-mp="2dm676r3sx6hf">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2439" data-mp="2ik7szjsm3jwl">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-2440" data-mp="1zngdzmk3rqyc">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-2441" data-mp="1kx93pu491e8q">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2442" data-mp="1pwllw1k6dx4z">
              <div className="flex items-center gap-3 mb-4" data-id="element-2443" data-mp="3drqgdss3b228">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2444" data-mp="38vrx2jlmca8o">
                  <Trash2 size={24} className="text-red-600" data-id="element-2445" data-mp="34u6hgqhp9hus" />
                </div>
                <div data-id="element-2446" data-mp="5e9rkitbjfbf">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2447" data-mp="5cqdmyv4xgct">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-2448" data-mp="cu4kl7kyyqe">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-2449" data-mp="3niuxay1bswzp">
                <p className="text-sm text-red-800" data-id="element-2450" data-mp="2h4lu7veijyfp">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-2451" data-mp="wl09m9n1pcq2">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2452" data-mp="wlrsqkzghhzg">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-2453" data-mp="1ad6ntq6n19mv">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}